import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, ArrowRight, Wand2, Image as ImageIcon, Video, CheckCircle2, Rocket, ShieldCheck, LayoutTemplate, Tag, Download, Gift, Facebook, MapPin, Radar, Tv, Copy } from "lucide-react";
import { sr } from "../../lib/sr";
import AdvancedTargeting, { targetingSummary } from "../../components/smartreach/AdvancedTargeting";

const STEPS = [
  { key: "plan", label: "Drafting the campaign plan" },
  { key: "copy", label: "Writing ad copy" },
  { key: "image", label: "Designing a banner image" },
  { key: "video", label: "Writing a video script" },
];

// Coerce any LLM value (string | object | array) into a display string.
const asText = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(" · ");
  if (typeof v === "object") return Object.values(v).map(asText).filter(Boolean).join(" — ");
  return String(v);
};

export default function AICampaignBuilder() {
  const nav = useNavigate();
  const [form, setForm] = useState({ goal: "", offer: "", brief: "", daily_spend: 100, duration_days: 30, channel: "Display", conquesting: false });
  const [templates, setTemplates] = useState([]);
  const [vertical, setVertical] = useState(null);
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [geoRadius, setGeoRadius] = useState("");
  const [geoZips, setGeoZips] = useState("");
  const [geoDmas, setGeoDmas] = useState([]);
  const [dmaOptions, setDmaOptions] = useState([]);
  const [dmaSearch, setDmaSearch] = useState("");
  const [ctvOptions, setCtvOptions] = useState([]);
  const [ctvSelected, setCtvSelected] = useState([]);
  const [lookalike, setLookalike] = useState(false);
  const [lookalikeSource, setLookalikeSource] = useState("customers");
  const [targeting, setTargeting] = useState({ mode: "audience", conquesting: false, audienceIds: [], audienceNames: [], contextualIds: [], contextualNames: [] });
  const geoObj = useMemo(() => {
    const zips = geoZips.split(/[\s,]+/).map((z) => z.trim()).filter(Boolean);
    const g = {};
    if (Number(geoRadius) > 0) g.radius_miles = Number(geoRadius);
    if (zips.length) g.zips = zips;
    if (geoDmas.length) g.dmas = geoDmas;
    return Object.keys(g).length ? g : null;
  }, [geoRadius, geoZips, geoDmas]);
  const [pixel, setPixel] = useState(null);
  const [showPixel, setShowPixel] = useState(false);
  const [compliance, setCompliance] = useState(false);
  const [regulated, setRegulated] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({}); // key -> 'running'|'done'|'skipped'
  const [result, setResult] = useState(null); // {plan, concepts, banner, video}
  const [activating, setActivating] = useState(false);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [creditBudget, setCreditBudget] = useState(3000);
  const [quoteResult, setQuoteResult] = useState(null);
  const [creditApplied, setCreditApplied] = useState(false);
  const [creditDismissed, setCreditDismissed] = useState(false);
  // Meta (Facebook & Instagram) add-on recommendation
  const [meta, setMeta] = useState(null); // {configured, connections} | {blocked:true}
  const [metaBusy, setMetaBusy] = useState(false);
  const [metaDone, setMetaDone] = useState(false);
  const [metaForm, setMetaForm] = useState({ concept: 0, objective: "OUTCOME_TRAFFIC", daily: 50, link: "", connectionId: "", ab: false });

  useEffect(() => {
    if (result) sr.creditWallet().then(setWallet).catch(() => {});
  }, [result]);

  const applyCredit = async () => {
    try {
      const q = await sr.creditQuote({ campaign_value: Number(creditBudget) || 0, use_credits: true });
      setQuoteResult(q);
      if (q.blocked) { toast.error(q.block_reason || "Credit can't be applied to this campaign amount"); setCreditApplied(false); }
      else { setCreditApplied(true); toast.success("SmartReach credit applied to your quote"); }
    } catch (e) { toast.error("Could not apply credit"); }
  };

  useEffect(() => {
    sr.campaignTemplates().then((r) => {
      setTemplates(r.templates || []);
      setRegulated(!!r.my_category_regulated);
    }).catch(() => {});
    sr.audiencePresets().then((r) => setSegments(r.segments || [])).catch(() => {});
    sr.ctvChannels().then((r) => setCtvOptions(r.channels || [])).catch(() => {});
    sr.dmaList().then((r) => { setDmaOptions(r.dmas || []); if (r.default_radius_miles) setGeoRadius(String(r.default_radius_miles)); }).catch(() => {});
    sr.getPixel().then(setPixel).catch(() => {});
  }, []);

  // Healthcare = the business category is regulated, or a compliance template was applied.
  const healthcare = compliance || regulated;
  const CHANNELS = [
    { v: "Display", ok: true, hc: "Contextual Display" },
    { v: "Social", ok: false, hc: null },
    { v: "Video", ok: true, hc: "Contextual Video / CTV" },
    { v: "Native", ok: true, hc: "Contextual Native" },
    { v: "Radio", ok: true, hc: "Contextual Audio / Radio" },
  ];

  // Never leave a non-compliant channel selected for a healthcare advertiser.
  useEffect(() => {
    if (healthcare && form.channel === "Social") setForm((f) => ({ ...f, channel: "Display" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthcare]);

  // When a result is ready, decide whether we can also offer Meta (Facebook & Instagram).
  useEffect(() => {
    if (!result) { setMeta(null); setMetaDone(false); return; }
    if (healthcare) { setMeta({ blocked: true }); return; }
    Promise.all([
      sr.metaStatus().catch(() => null),
      sr.metaConnections().catch(() => ({ connections: [] })),
      sr.profileGet().catch(() => ({})),
    ]).then(([s, c, p]) => {
      const conns = (c?.connections || []).filter((x) => x.ad_account_id && x.page_id);
      setMeta({ configured: !!s?.configured, connections: conns });
      setMetaForm((f) => ({
        ...f,
        connectionId: f.connectionId || (conns[0]?.id || ""),
        daily: Math.max(1, Math.round(Number(form.daily_spend) || 50)),
        link: f.link || p?.website || "",
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const createMetaDraft = async () => {
    const conns = meta?.connections || [];
    if (!conns.length) { nav("/app/meta-ads"); return; }
    const cid = metaForm.connectionId || conns[0].id;
    const concepts = result.concepts || [];
    const toVariant = (c) => ({
      primary_text: [asText(c.headline), asText(c.body)].filter(Boolean).join("\n\n") || form.goal.trim(),
      headline: asText(c.headline), link_url: metaForm.link, cta: "LEARN_MORE",
    });
    const body = {
      connection_id: cid,
      name: (form.goal.trim() || "AI campaign").slice(0, 60),
      objective: metaForm.objective,
      audience: { countries: ["US"], age_min: 25, age_max: 55 },
      budget: { type: "daily", amount: Number(metaForm.daily) || 50 },
    };
    if (metaForm.ab && concepts.length > 1) {
      body.creative_variants = concepts.map(toVariant);
    } else {
      body.creative = toVariant(concepts[metaForm.concept] || {});
    }
    setMetaBusy(true);
    try {
      await sr.metaCreateDraft(body);
      setMetaDone(true);
      toast.success(metaForm.ab ? "Meta A/B draft created — review & publish in Meta Ads" : "Meta draft created — review & publish in Meta Ads");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the Meta draft");
    } finally {
      setMetaBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyTemplate = (t) => {
    setForm({ goal: t.title, offer: t.offer || "", brief: "", daily_spend: Math.max(1, Math.round((t.budget || 3000) / 30)), duration_days: 30, channel: t.channel || "Display", conquesting: false });
    setVertical(t.vertical);
    setCompliance(!!t.compliance);
    toast.success(`Loaded the ${t.vertical} template`);
  };

  const generate = async () => {
    if (!form.goal.trim()) return toast.error("Tell the AI your goal first");
    setRunning(true);
    setResult(null);
    setProgress({ plan: "running" });
    const out = {};
    try {
      const monthly = Math.max(1, Math.round((Number(form.daily_spend) || 0) * 30));
      const pr = await sr.generatePlan({
        goal: form.goal.trim(), secondary: [], monthly_budget: monthly, vertical,
        daily_spend: Number(form.daily_spend), duration_days: Number(form.duration_days),
        conquesting: !!targeting.conquesting,
        audience_segments: [...selectedSegments, ...targeting.audienceIds],
        contextual_segments: targeting.contextualIds,
        targeting_summary: targetingSummary(targeting),
        geo_targeting: geoObj,
        lookalike, lookalike_source: lookalike ? lookalikeSource : null,
        ctv_channels: ctvSelected,
        brief: form.brief.trim() || null,
      });
      out.plan = pr.growth_plan;
      setProgress({ plan: "done", copy: "running" });

      const offer = form.offer.trim() || (out.plan?.plan?.offer?.headline || "");
      const cr = await sr.generateCreative({ channel: form.channel, goal: form.goal.trim(), offer, count: 3, vertical });
      out.concepts = cr.creatives || [];
      setProgress({ plan: "done", copy: "done", image: "running" });

      try {
        const im = await sr.generateImage({ channel: form.channel });
        out.banner = im.ok ? im.creative : null;
        setProgress({ plan: "done", copy: "done", image: im.ok ? "done" : "skipped", video: "running" });
      } catch {
        out.banner = null;
        setProgress({ plan: "done", copy: "done", image: "skipped", video: "running" });
      }

      const vs = await sr.generateVideoScript({ duration_sec: 15, goal: form.goal.trim() });
      out.video = vs.creative?.script || null;
      setProgress({ plan: "done", copy: "done", image: out.banner ? "done" : "skipped", video: "done" });

      setResult(out);
      toast.success("Your AI campaign is ready to review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — please try again");
    } finally {
      setRunning(false);
    }
  };

  const approveActivate = async () => {
    if (!result?.plan) return;
    setActivating(true);
    try {
      await sr.approvePlan(result.plan.id);
      const c0 = (result.concepts || [])[0] || {};
      await sr.addPlanCreative(result.plan.id, { source: "ai", headline: asText(c0.headline), body: asText(c0.body), cta: asText(c0.cta) });
      await sr.activatePlan(result.plan.id);
      toast.success("Approved & activated — campaigns created");
      nav("/app/campaigns");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    } finally {
      setActivating(false);
    }
  };

  const downloadCertificate = async () => {
    if (!result?.plan?.id) return;
    setDownloadingCert(true);
    try {
      const blob = await sr.certificatePdf(result.plan.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance-certificate-${String(result.plan.id).slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the compliance certificate");
    } finally {
      setDownloadingCert(false);
    }
  };

  const plan = result?.plan?.plan || {};
  const daily = Number(form.daily_spend) || 0;
  const duration = Number(form.duration_days) || 0;
  const monthlyEq = Math.round(daily * 30);
  const totalSpend = Math.round(daily * duration);
  const dmaFiltered = dmaOptions.filter((d) => d.name.toLowerCase().includes(dmaSearch.trim().toLowerCase()));
  const radiusMeters = Math.round((Number(geoRadius) || 0) / 0.000621371);

  return (
    <div data-testid="ai-campaign-builder">
      <div style={{ marginBottom: 20 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Sparkles size={12} /> AI Campaign Builder</span>
        <h1 style={{ fontSize: 30 }}>Create a campaign with AI</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Tell the AI your goal — it drafts the plan, writes the copy, designs a banner and writes a video script, all grounded in your business.</p>
      </div>

      {/* Start from a template */}
      {templates.length > 0 && (
        <div style={{ marginBottom: 20 }} data-testid="template-gallery">
          <div className="sr-eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <LayoutTemplate size={14} /> Start from a template
          </div>
          <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 6 }}>
            {templates.map((t) => (
              <button key={t.key} onClick={() => applyTemplate(t)} data-testid={`template-${t.key}`}
                style={{ flexShrink: 0, width: 230, textAlign: "left", background: "#fff", cursor: "pointer",
                  border: vertical === t.vertical ? "2px solid var(--blue)" : "1px solid var(--border)",
                  borderRadius: 14, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span className="sr-badge sr-badge-gray">{t.vertical}</span>
                  {t.compliance && <span className="sr-badge sr-badge-green" title="Compliance-guarded"><ShieldCheck size={11} /></span>}
                </div>
                <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 15, marginTop: 10 }}>{t.title}</div>
                <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>Target {t.goal_label} · {t.channel}</p>
                {t.offer && <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Offer: {t.offer}</p>}
              </button>
            ))}
          </div>
        </div>
      )}

      {healthcare && (
        <div className="sr-card" data-testid="ai-compliance-banner"
          style={{ background: "#F1FBF4", border: "1px solid #B6E3C6", display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16 }}>
          <ShieldCheck size={20} color="#067647" style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontSize: 13, color: "var(--navy)" }}>
            <strong>Compliance-guarded (Texas TDPSA/TMRPA + HIPAA-aware).</strong> The AI will build a contextual-only campaign —
            no behavioral retargeting or sensitive health-data targeting — and attach a compliance note to your plan.
          </p>
        </div>
      )}

      <div className="sr-card">
          <div className="sr-field">
            <label className="sr-label">Campaign goal</label>
            <input className="sr-input" placeholder="e.g. Book more first-time appointments this month" value={form.goal} onChange={set("goal")} data-testid="ai-goal" />
          </div>
          <div className="sr-field">
            <label className="sr-label">Offer (optional)</label>
            <input className="sr-input" placeholder="e.g. 20% off first visit" value={form.offer} onChange={set("offer")} data-testid="ai-offer" />
          </div>
          <div className="sr-field">
            <label className="sr-label">Targeting brief (optional)</label>
            <textarea className="sr-input" rows={3} placeholder="Tell the AI what this campaign should achieve — who to reach, where, and what result you want." value={form.brief} onChange={set("brief")} data-testid="ai-brief" />
          </div>
          <div className="sr-field">
            <label className="sr-label">Daily spend ($/day)</label>
            <input className="sr-input" type="number" min="1" value={form.daily_spend} onChange={set("daily_spend")} data-testid="ai-daily-spend" />
          </div>
          <div className="sr-field">
            <label className="sr-label">Campaign duration (days)</label>
            <input className="sr-input" type="number" min="1" value={form.duration_days} onChange={set("duration_days")} data-testid="ai-duration" />
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }} data-testid="ai-duration-rec">
              For the best results, we recommend running your campaign for at least <strong>30 days</strong>.
            </p>
          </div>
          <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)", padding: "10px 14px", marginBottom: 12 }} data-testid="ai-spend-summary">
            <span style={{ fontSize: 14, color: "var(--navy)" }}>
              ≈ <strong data-testid="ai-total-spend">${totalSpend.toLocaleString()}</strong> total over {duration} days · ≈ <strong>${monthlyEq.toLocaleString()}/mo</strong>
              {targeting.conquesting && <> · <span style={{ color: "#B8791B", fontWeight: 700 }}>+10% conquesting</span></>}
            </span>
          </div>
          <div className="sr-field">
            <label className="sr-label">Creative channel</label>
            <select className="sr-select" value={form.channel} onChange={set("channel")} data-testid="ai-channel">
              {CHANNELS.map((c) => {
                const blocked = healthcare && !c.ok;
                const label = healthcare && c.hc ? c.hc : c.v;
                return <option key={c.v} value={c.v} disabled={blocked}>{blocked ? `${c.v} — not available (HIPAA/state law)` : label}</option>;
              })}
            </select>
            {healthcare && (
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }} data-testid="ai-channel-compliance-note">
                <ShieldCheck size={13} color="#067647" /> Behavioral social targeting is disabled for healthcare — campaigns run contextual-only to stay HIPAA &amp; state-law compliant.
              </p>
            )}
          </div>
          {segments.length > 0 && (
            <div className="sr-field" data-testid="ai-audience-presets">
              <label className="sr-label">Audience segments <span className="sr-muted" style={{ fontWeight: 400 }}>· suggested for your business (planning estimates)</span></label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                {segments.map((s) => {
                  const on = selectedSegments.includes(s.id);
                  return (
                    <button type="button" key={s.id} onClick={() => setSelectedSegments((cur) => on ? cur.filter((x) => x !== s.id) : [...cur, s.id])}
                      data-testid={`ai-segment-${s.id}`} title={s.description}
                      style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                      {s.name} <span style={{ opacity: 0.75 }}>· ~{Number(s.size_estimate).toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="sr-field" data-testid="ai-geo">
            <label className="sr-label"><MapPin size={13} /> Geo targeting <span className="sr-muted" style={{ fontWeight: 400 }}>· combine any of these</span></label>
            <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 90 }}>Radius</span>
                <input className="sr-input" type="number" min="0" step="0.001" value={geoRadius} onChange={(e) => setGeoRadius(e.target.value)} data-testid="ai-geo-radius" style={{ width: 120 }} />
                <span className="sr-muted" style={{ fontSize: 12 }}>miles{Number(geoRadius) > 0 && <> · ≈ {radiusMeters.toLocaleString()} m</>} around each store</span>
                <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[0.5, 1, 5, 10, 25].map((m) => (
                    <button type="button" key={m} onClick={() => setGeoRadius(String(m))} data-testid={`ai-geo-radius-${m}`}
                      style={{ border: "1px solid var(--border)", background: "#fff", borderRadius: 999, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>{m} mi</button>
                  ))}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 90 }}>Zip codes</span>
                <input className="sr-input" value={geoZips} onChange={(e) => setGeoZips(e.target.value)} placeholder="e.g. 75070, 75071, 75025" data-testid="ai-geo-zips" style={{ flex: 1, minWidth: 200 }} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 90 }}>DMA / metro</span>
                  <input className="sr-input" value={dmaSearch} onChange={(e) => setDmaSearch(e.target.value)} placeholder="Search metros…" data-testid="ai-geo-dma-search" style={{ flex: 1, minWidth: 200 }} />
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxHeight: 120, overflowY: "auto" }}>
                  {dmaFiltered.slice(0, 24).map((d) => {
                    const on = geoDmas.includes(d.code);
                    return (
                      <button type="button" key={d.code} onClick={() => setGeoDmas((cur) => on ? cur.filter((x) => x !== d.code) : [...cur, d.code])}
                        data-testid={`ai-geo-dma-${d.code}`}
                        style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>{d.name}</button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <AdvancedTargeting geo={geoObj} value={targeting} onChange={setTargeting} />
          <div className="sr-field" data-testid="ai-lookalike">
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={lookalike} onChange={(e) => setLookalike(e.target.checked)} data-testid="ai-lookalike-toggle" style={{ marginTop: 3 }} />
              <span>
                <strong style={{ color: "var(--navy)" }}>Build a lookalike audience</strong>
                <div className="sr-muted" style={{ fontSize: 12 }}>Find new people who resemble your best customers or recent website visitors.</div>
              </span>
            </label>
            {lookalike && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, marginLeft: 28 }}>
                {[["customers", "My best customers"], ["visitors", "My website visitors"]].map(([v, label]) => (
                  <button type="button" key={v} onClick={() => setLookalikeSource(v)} data-testid={`ai-lookalike-${v}`}
                    style={{ border: "1px solid " + (lookalikeSource === v ? "var(--blue)" : "var(--border)"), background: lookalikeSource === v ? "var(--blue)" : "#fff", color: lookalikeSource === v ? "#fff" : "var(--navy)", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>
                ))}
              </div>
            )}
            <div style={{ marginTop: 8, marginLeft: 28 }}>
              <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setShowPixel((v) => !v)} data-testid="ai-pixel-toggle">
                <Radar size={13} /> {showPixel ? "Hide" : "Get"} your website pixel
              </button>
              {showPixel && pixel && (
                <div style={{ marginTop: 8, background: "var(--blue-light)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }} data-testid="ai-pixel-card">
                  <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>
                    Install this on every page of your website. {pixel.installed ? <span style={{ color: "var(--success)", fontWeight: 700 }}>✓ Active — {pixel.stats.page_views.toLocaleString()} page views tracked</span> : "Not detected yet."}
                  </div>
                  <pre style={{ background: "#0f172a", color: "#e2e8f0", padding: 10, borderRadius: 6, fontSize: 11, overflowX: "auto", whiteSpace: "pre-wrap" }} data-testid="ai-pixel-snippet">{pixel.snippet}</pre>
                  <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { navigator.clipboard?.writeText(pixel.snippet); toast.success("Pixel snippet copied"); }} data-testid="ai-pixel-copy"><Copy size={13} /> Copy snippet</button>
                </div>
              )}
            </div>
          </div>
          {ctvOptions.length > 0 && (
            <div className="sr-field" data-testid="ai-ctv">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <label className="sr-label" style={{ marginBottom: 0 }}><Tv size={13} /> Connected TV apps &amp; channels</label>
                <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" data-testid="ai-ctv-select-all"
                  onClick={() => setCtvSelected(ctvSelected.length === ctvOptions.length ? [] : ctvOptions.map((c) => c.id))}>
                  {ctvSelected.length === ctvOptions.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                {ctvOptions.map((c) => {
                  const on = ctvSelected.includes(c.id);
                  return (
                    <button type="button" key={c.id} onClick={() => setCtvSelected((cur) => on ? cur.filter((x) => x !== c.id) : [...cur, c.id])}
                      data-testid={`ai-ctv-${c.id}`} title={c.category}
                      style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{c.name}</button>
                  );
                })}
              </div>
            </div>
          )}
        <button className="sr-btn sr-btn-primary" style={{ marginTop: 14 }} onClick={generate} disabled={running} data-testid="ai-generate">
          {running ? <><Loader2 size={16} className="sr-spin" /> Generating…</> : <><Wand2 size={16} /> Generate with AI</>}
        </button>
      </div>

      {(running || result) && (
        <div className="sr-card" style={{ marginTop: 16 }} data-testid="ai-progress">
          {STEPS.map((s) => {
            const st = progress[s.key];
            return (
              <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 14, opacity: st ? 1 : 0.4 }}>
                {st === "running" && <Loader2 size={16} className="sr-spin" style={{ color: "var(--blue)" }} />}
                {st === "done" && <CheckCircle2 size={16} style={{ color: "var(--success)" }} />}
                {st === "skipped" && <span className="sr-badge sr-badge-gray">skipped</span>}
                {!st && <span style={{ width: 16 }} />}
                {s.label}
              </div>
            );
          })}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 20, display: "grid", gap: 16 }}>
          {/* Plan */}
          <div className="sr-card" data-testid="ai-result-plan">
            <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Campaign plan · v{result.plan.version}</div>
            {plan.diagnosis && <p style={{ fontSize: 14 }}>{plan.diagnosis}</p>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              {(plan.channel_mix || []).map((c, i) => (
                <span key={i} className="sr-badge">{c.channel} · {c.budget_pct}%</span>
              ))}
            </div>
            {plan.offer?.headline && <p className="sr-muted" style={{ fontSize: 13, marginTop: 10 }}>Offer: <strong style={{ color: "var(--navy)" }}>{plan.offer.headline}</strong></p>}
            {plan.compliance && (
              <div style={{ marginTop: 12, background: "#F1FBF4", border: "1px solid #B6E3C6", borderRadius: 12, padding: 12 }} data-testid="ai-plan-compliance">
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#067647", fontWeight: 700, fontSize: 13 }}>
                  <ShieldCheck size={16} /> {plan.compliance.framework}
                </div>
                <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>{plan.compliance.summary}</p>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="ai-view-certificate"
                  onClick={() => nav(`/app/compliance-certificate/${result.plan.id}`)}>
                  <ShieldCheck size={14} /> View compliance certificate
                </button>
                <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 8 }} data-testid="ai-download-certificate"
                  onClick={downloadCertificate} disabled={downloadingCert}>
                  {downloadingCert ? <Loader2 size={14} className="sr-spin" /> : <Download size={14} />} Download certificate (PDF)
                </button>
              </div>
            )}
            {plan.forecast && (
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                Forecast: {plan.forecast.metric} {plan.forecast.low}–{plan.forecast.high} {plan.forecast.unit} · confidence {plan.forecast.confidence}
              </p>
            )}
            <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 14 }} onClick={approveActivate} disabled={activating} data-testid="ai-approve-activate">
              {activating ? <Loader2 size={15} className="sr-spin" /> : <><Rocket size={15} /> Approve & activate</>}
            </button>
          </div>

          {/* Meta (Facebook & Instagram) add-on recommendation */}
          {meta && !meta.blocked && (
            <div className="sr-card" data-testid="ai-meta-recommend" style={{ borderLeft: "3px solid #1877F2" }}>
              <div className="sr-eyebrow" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <Facebook size={14} color="#1877F2" /> Also run on Meta — Facebook &amp; Instagram
              </div>
              {(!meta.configured || (meta.connections || []).length === 0) ? (
                <>
                  <p style={{ fontSize: 14, color: "var(--navy)" }}>
                    Want to release this campaign on <strong>Facebook &amp; Instagram</strong> too? Connect your Meta ad account
                    and I'll turn this AI creative into a Meta campaign.
                  </p>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} data-testid="ai-meta-connect" onClick={() => nav("/app/meta-ads")}>
                    <Facebook size={14} /> Connect Meta
                  </button>
                </>
              ) : metaDone ? (
                <div data-testid="ai-meta-done">
                  <p style={{ fontSize: 14, color: "var(--navy)" }}>
                    <CheckCircle2 size={16} style={{ color: "var(--success)", verticalAlign: "-3px" }} /> Meta draft created — it stays <strong>PAUSED</strong> (no spend) until you activate it.
                  </p>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} data-testid="ai-meta-open" onClick={() => nav("/app/meta-ads")}>
                    Review &amp; publish in Meta Ads <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 14, color: "var(--navy)" }}>
                    Yes — release these ads on <strong>Facebook &amp; Instagram</strong> as well. Pick the account, concept and goal and I'll build a Meta draft
                    (created <strong>PAUSED</strong>, no spend until you activate).
                  </p>
                  {meta.connections.length > 1 && (
                    <div className="sr-field" style={{ marginTop: 10 }}>
                      <label className="sr-label">Meta account</label>
                      <select className="sr-select" value={metaForm.connectionId} onChange={(e) => setMetaForm({ ...metaForm, connectionId: e.target.value })} data-testid="ai-meta-connection">
                        {meta.connections.map((c) => <option key={c.id} value={c.id}>{c.page_name || c.ad_account_name || c.ad_account_id} {c.instagram_username ? `· @${c.instagram_username}` : ""}</option>)}
                      </select>
                    </div>
                  )}
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 14, color: "var(--navy)", cursor: "pointer" }} data-testid="ai-meta-ab-label">
                    <input type="checkbox" checked={metaForm.ab} onChange={(e) => setMetaForm({ ...metaForm, ab: e.target.checked })} data-testid="ai-meta-ab" />
                    Push all {(result.concepts || []).length} concepts as an A/B test (Meta picks the winner)
                  </label>
                  {!metaForm.ab && (
                    <div className="sr-field" style={{ marginTop: 10 }}>
                      <label className="sr-label">Use which ad concept?</label>
                      <select className="sr-select" value={metaForm.concept} onChange={(e) => setMetaForm({ ...metaForm, concept: Number(e.target.value) })} data-testid="ai-meta-concept">
                        {(result.concepts || []).map((c, i) => <option key={i} value={i}>{asText(c.headline) || `Concept ${i + 1}`}</option>)}
                      </select>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div className="sr-field" style={{ flex: 1, minWidth: 200 }}>
                      <label className="sr-label">Goal (objective)</label>
                      <select className="sr-select" value={metaForm.objective} onChange={(e) => setMetaForm({ ...metaForm, objective: e.target.value })} data-testid="ai-meta-objective">
                        <option value="OUTCOME_TRAFFIC">Traffic — send clicks to your page</option>
                        <option value="OUTCOME_LEADS">Leads — collect leads on Meta</option>
                        <option value="OUTCOME_ENGAGEMENT">Engagement</option>
                        <option value="OUTCOME_AWARENESS">Awareness</option>
                        <option value="OUTCOME_SALES">Sales</option>
                      </select>
                    </div>
                    <div className="sr-field">
                      <label className="sr-label">Daily budget ($)</label>
                      <input className="sr-input" type="number" style={{ width: 120 }} value={metaForm.daily} onChange={(e) => setMetaForm({ ...metaForm, daily: e.target.value })} data-testid="ai-meta-budget" />
                    </div>
                  </div>
                  <div className="sr-field">
                    <label className="sr-label">Destination link</label>
                    <input className="sr-input" placeholder="https://…" value={metaForm.link} onChange={(e) => setMetaForm({ ...metaForm, link: e.target.value })} data-testid="ai-meta-link" />
                  </div>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 6 }} onClick={createMetaDraft} disabled={metaBusy} data-testid="ai-meta-create">
                    {metaBusy ? <Loader2 size={14} className="sr-spin" /> : <><Facebook size={14} /> Create Meta ads (Facebook &amp; Instagram)</>}
                  </button>
                </>
              )}
            </div>
          )}
          {meta?.blocked && (
            <div className="sr-card" data-testid="ai-meta-blocked" style={{ borderLeft: "3px solid #B8791B" }}>
              <div className="sr-eyebrow" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <ShieldCheck size={14} color="#B8791B" /> Meta (Facebook &amp; Instagram) unavailable
              </div>
              <p className="sr-muted" style={{ fontSize: 13 }}>
                Behavioral social targeting on Meta is disabled for healthcare / regulated advertisers to stay HIPAA &amp; state-law compliant. This campaign runs contextual-only.
              </p>
            </div>
          )}

          {/* AI Credit Prompt */}
          {wallet && wallet.available > 0 && !creditDismissed && (
            <div className="sr-card" data-testid="ai-credit-prompt" style={{ borderLeft: "3px solid #F2A33D" }}>
              <div className="sr-eyebrow" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Gift size={14} /> SmartReach Credit available</div>
              <p style={{ fontSize: 14, color: "var(--navy)" }}>
                You have <strong data-testid="ai-credit-available">${wallet.available.toLocaleString()}</strong> in SmartReach marketing credit that's eligible for this campaign. Want me to apply it?
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                <label className="sr-muted" style={{ fontSize: 13 }}>Monthly budget $</label>
                <input className="sr-input" style={{ width: 130 }} type="number" value={creditBudget}
                  onChange={(e) => { setCreditBudget(e.target.value); setCreditApplied(false); setQuoteResult(null); }} data-testid="ai-credit-budget" />
              </div>
              {creditApplied && quoteResult ? (
                <div data-testid="ai-credit-applied" style={{ marginTop: 12, background: "#FFF9F0", border: "1px solid #F2D9B0", borderRadius: 12, padding: 12 }}>
                  <p style={{ fontSize: 13, color: "var(--navy)" }}>
                    Campaign value <strong>${quoteResult.campaign_value.toLocaleString()}</strong> · Credit applied <strong style={{ color: "#B8791B" }}>−${quoteResult.credit_applied.toLocaleString()}</strong>
                    {quoteResult.coupon_discount ? <> · Coupon −${quoteResult.coupon_discount.toLocaleString()}</> : null}
                  </p>
                  <p style={{ fontSize: 15, fontWeight: 800, color: "var(--navy)", marginTop: 4 }}>Cash due at checkout: ${quoteResult.cash_due.toLocaleString()}</p>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 8 }} data-testid="ai-credit-remove"
                    onClick={() => { setCreditApplied(false); setQuoteResult(null); }}>Remove credit</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={applyCredit} data-testid="ai-apply-credit"><Gift size={14} /> Apply credit</button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setCreditDismissed(true)} data-testid="ai-skip-credit">Don't use it</button>
                </div>
              )}
            </div>
          )}

          {/* Offer & Coupon step */}
          <div className="sr-card" data-testid="ai-offer-coupon-step" style={{ borderLeft: "3px solid var(--blue)" }}>
            <div className="sr-eyebrow" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Tag size={14} /> Offer & Coupon</div>
            <p style={{ fontSize: 14, color: "var(--navy)" }}>
              Goals like visits, leads, purchases, loyalty or competitor conquesting convert far better with a coupon.
              Design an AI coupon offer with a hosted landing page, display banners and Apple/Google Wallet passes — then attach it to this campaign.
            </p>
            <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} data-testid="ai-add-offer"
              onClick={() => nav(`/app/offers/new?goal=${encodeURIComponent(form.goal.trim())}`)}>
              <Sparkles size={14} /> Design a coupon offer
            </button>
          </div>

          <div className="sr-card" data-testid="ai-result-copy">
            <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Ad copy</div>
            <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))" }}>
              {(result.concepts || []).map((c, i) => (
                <div key={i} className="sr-card" style={{ background: "var(--blue-light)" }}>
                  {c.angle && <span className="sr-badge sr-badge-gray" style={{ marginBottom: 6 }}>{asText(c.angle)}</span>}
                  <div style={{ color: "var(--navy)", fontWeight: 700 }}>{asText(c.headline)}</div>
                  <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{asText(c.body)}</p>
                  <span className="sr-badge" style={{ marginTop: 8 }}>{asText(c.cta)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Banner */}
          <div className="sr-card" data-testid="ai-result-banner">
            <div className="sr-eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><ImageIcon size={14} /> Banner image</div>
            {result.banner?.image_data ? (
              <img src={result.banner.image_data} alt="AI banner" style={{ maxWidth: "100%", borderRadius: 12, border: "1px solid var(--border)" }} data-testid="ai-banner-img" />
            ) : (
              <p className="sr-muted" style={{ fontSize: 13 }}>Image generation was skipped or unavailable — copy and video script are ready.</p>
            )}
          </div>

          {/* Video script */}
          {result.video && (
            <div className="sr-card" data-testid="ai-result-video">
              <div className="sr-eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><Video size={14} /> Video script · {asText(result.video.title)}</div>
              <div style={{ display: "grid", gap: 8 }}>
                {(result.video.scenes || []).map((s, i) => (
                  <div key={i} style={{ fontSize: 13, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                    <strong style={{ color: "var(--navy)" }}>Scene {i + 1}:</strong> {asText(s.visual ?? s.on_screen ?? s)}
                    {(s.voiceover || s.vo) && <div className="sr-muted" style={{ marginTop: 2 }}>VO: “{asText(s.voiceover ?? s.vo)}”</div>}
                  </div>
                ))}
              </div>
              {result.video.cta && <span className="sr-badge" style={{ marginTop: 10 }}>{asText(result.video.cta)}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
